import React from 'react';
import {
    Box, TextField, Button, Divider, Alert, Typography, Chip,
    Switch, FormControlLabel, MenuItem, Select, FormControl, InputLabel,
} from '@mui/material';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import RefreshIcon from '@mui/icons-material/Refresh';
import SendIcon from '@mui/icons-material/Send';
import SectionTitle from './SectionTitle';

/**
 * Credenciais não voltam do servidor — nem mascaradas. O formulário só informa
 * se existe algo gravado; digitar substitui, deixar vazio preserva.
 */
const textoCredencial = (configurado) => (configurado ? 'Credencial configurada' : 'Não configurada');
const ajudaCredencial = (configurado) => (configurado
    ? 'Configurada. Deixe vazio para manter; digite para substituir.'
    : 'Não configurada.');

function StatusChip({ state }) {
    const map = {
        open: { label: 'Conectado', color: 'success' },
        connecting: { label: 'Conectando...', color: 'info' },
        close: { label: 'Desconectado', color: 'error' },
        not_created: { label: 'Não criada', color: 'default' },
    };
    const cfg = map[state] || { label: state || 'desconhecido', color: 'warning' };
    return <Chip size="small" label={cfg.label} color={cfg.color} />;
}

export default function SecaoIntegracoes({
    parametros, handleChange,
    evoStatus, evoQrcode, evoLoading, evoSendData, setEvoSendData, evoSending,
    carregarEvoQrCode, carregarEvoStatus, enviarMensagemTeste,
    testeData, setTesteData, testeSending, enviarTesteNotificacao,
}) {
    const handleTesteChange = (field) => (e) =>
        setTesteData(s => ({ ...s, [field]: e.target.value }));

    return (
        <Box>
            {/* Google Meet */}
            <SectionTitle descricao="Credenciais OAuth2 do console.cloud.google.com — necessárias para criar links de Meet automaticamente nas sessões online.">
                Google Meet
            </SectionTitle>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2 }}>
                <TextField fullWidth label="Client ID" name="googleClientId"
                    value={parametros.googleClientId} onChange={handleChange}
                    placeholder="xxxxx.apps.googleusercontent.com" />
                <TextField fullWidth label="Client Secret" name="googleClientSecret" type="password"
                    value={parametros.googleClientSecret} onChange={handleChange}
                    placeholder={textoCredencial(parametros.googleClientSecretConfigurado)}
                    helperText={ajudaCredencial(parametros.googleClientSecretConfigurado)} />
            </Box>
            <TextField fullWidth label="Refresh Token" name="googleRefreshToken" type="password"
                value={parametros.googleRefreshToken} onChange={handleChange}
                placeholder={textoCredencial(parametros.googleRefreshTokenConfigurado)}
                helperText={`Gerado uma vez ao autorizar o app com sua conta Google. ${ajudaCredencial(parametros.googleRefreshTokenConfigurado)}`} />

            <Divider sx={{ my: 4 }} />

            {/* E-mail SMTP */}
            <SectionTitle descricao="Configure o servidor SMTP para envio de notificações por e-mail (aceites, contratos, renovações).">
                E-mail (SMTP)
            </SectionTitle>

            <FormControlLabel
                control={<Switch name="emailAtivo" checked={!!parametros.emailAtivo} onChange={handleChange} sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#1ABB9C' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#1ABB9C' } }} />}
                label="Ativar envio de e-mail"
                sx={{ mb: 2 }}
            />

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 2, mb: 2 }}>
                <TextField fullWidth label="Host SMTP" name="smtpHost"
                    value={parametros.smtpHost} onChange={handleChange}
                    placeholder="smtp.gmail.com" disabled={!parametros.emailAtivo} />
                <TextField fullWidth label="Porta" name="smtpPorta" type="number"
                    value={parametros.smtpPorta} onChange={handleChange}
                    placeholder="587" disabled={!parametros.emailAtivo} />
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2 }}>
                <TextField fullWidth label="Usuário SMTP" name="smtpUsuario"
                    value={parametros.smtpUsuario} onChange={handleChange}
                    disabled={!parametros.emailAtivo} />
                <TextField fullWidth label="Senha SMTP" name="smtpSenha" type="password"
                    value={parametros.smtpSenha} onChange={handleChange}
                    placeholder={textoCredencial(parametros.smtpSenhaConfigurada)}
                    helperText={ajudaCredencial(parametros.smtpSenhaConfigurada)}
                    disabled={!parametros.emailAtivo} />
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2 }}>
                <TextField fullWidth label="E-mail remetente" name="emailRemetente"
                    value={parametros.emailRemetente} onChange={handleChange}
                    placeholder="sistema@clinica.com.br" disabled={!parametros.emailAtivo} />
                <TextField fullWidth label="Nome do remetente" name="emailNomeRemetente"
                    value={parametros.emailNomeRemetente} onChange={handleChange}
                    placeholder="SIGAPSI – Clínica" disabled={!parametros.emailAtivo} />
            </Box>
            <Box sx={{ display: 'flex', gap: 3, mb: 2, flexWrap: 'wrap' }}>
                <FormControlLabel
                    control={<Switch name="smtpStartTls" checked={!!parametros.smtpStartTls} onChange={handleChange} disabled={!parametros.emailAtivo} sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#1ABB9C' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#1ABB9C' } }} />}
                    label="STARTTLS" />
                <FormControlLabel
                    control={<Switch name="smtpSslEnable" checked={!!parametros.smtpSslEnable} onChange={handleChange} disabled={!parametros.emailAtivo} sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#1ABB9C' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#1ABB9C' } }} />}
                    label="SSL" />
                <TextField label="Timeout (ms)" name="smtpTimeoutMs" type="number"
                    value={parametros.smtpTimeoutMs} onChange={handleChange}
                    sx={{ width: 140 }} disabled={!parametros.emailAtivo} />
            </Box>
            <TextField fullWidth label="URL pública base" name="urlPublicaBase"
                value={parametros.urlPublicaBase} onChange={handleChange}
                placeholder="https://meusite.com.br"
                helperText="Usada para gerar links de aceite e renovação enviados por e-mail/WhatsApp." />

            <Divider sx={{ my: 4 }} />

            {/* WhatsApp */}
            <SectionTitle descricao="Configure a instância, gere o QR Code e leia com o WhatsApp do celular em Aparelhos conectados.">
                WhatsApp (Evolution API)
            </SectionTitle>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                <TextField label="Nome da instância" name="evolutionInstanceName"
                    value={parametros.evolutionInstanceName} onChange={handleChange}
                    placeholder="sigapsi-clinica" sx={{ flex: '1 1 240px' }} />
                {evoStatus && <StatusChip state={evoStatus.state} />}
            </Box>

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
                <Button variant="outlined" startIcon={<QrCode2Icon />}
                    onClick={carregarEvoQrCode} disabled={evoLoading}
                    sx={{ borderColor: '#1ABB9C', color: '#1ABB9C' }}>
                    {evoLoading ? 'Gerando...' : 'Gerar QR Code'}
                </Button>
                <Button variant="outlined" startIcon={<RefreshIcon />} onClick={carregarEvoStatus}
                    sx={{ borderColor: '#1ABB9C', color: '#1ABB9C' }}>
                    Verificar status
                </Button>
            </Box>

            {evoQrcode?.base64 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, mb: 3, p: 2, bgcolor: '#f9f9f9', borderRadius: 2 }}>
                    <img src={evoQrcode.base64.startsWith('data:') ? evoQrcode.base64 : `data:image/png;base64,${evoQrcode.base64}`}
                        alt="QR Code WhatsApp" style={{ width: 240, height: 240 }} />
                    {evoQrcode.pairingCode && (
                        <Typography variant="caption">
                            Código de pareamento: <strong>{evoQrcode.pairingCode}</strong>
                        </Typography>
                    )}
                </Box>
            )}

            <Alert severity="info" sx={{ mb: 2 }}>
                Teste rápido: envie uma mensagem após pareamento concluído.
            </Alert>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 2fr auto' }, gap: 2, alignItems: 'start' }}>
                <TextField size="small" label="Número (5544...)"
                    value={evoSendData.numero}
                    onChange={(e) => setEvoSendData(s => ({ ...s, numero: e.target.value.replace(/\D/g, '') }))} />
                <TextField size="small" label="Mensagem"
                    value={evoSendData.mensagem}
                    onChange={(e) => setEvoSendData(s => ({ ...s, mensagem: e.target.value }))} />
                <Button variant="contained" startIcon={<SendIcon />}
                    onClick={enviarMensagemTeste} disabled={evoSending}
                    sx={{ bgcolor: '#1ABB9C', '&:hover': { bgcolor: '#129A80' }, height: 40 }}>
                    {evoSending ? 'Enviando...' : 'Enviar'}
                </Button>
            </Box>

            <Divider sx={{ my: 4 }} />

            {/* Teste de notificação */}
            <SectionTitle descricao="Envie uma notificação de teste para validar e-mail ou WhatsApp antes do uso em produção. O resultado é registrado no histórico.">
                Teste de notificação
            </SectionTitle>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2 }}>
                <FormControl fullWidth size="small">
                    <InputLabel>Canal</InputLabel>
                    <Select label="Canal" value={testeData?.canal ?? 'EMAIL'} onChange={(e) => setTesteData(s => ({ ...s, canal: e.target.value }))}>
                        <MenuItem value="EMAIL">E-mail</MenuItem>
                        <MenuItem value="WHATSAPP">WhatsApp</MenuItem>
                    </Select>
                </FormControl>
                <FormControl fullWidth size="small">
                    <InputLabel>Tipo</InputLabel>
                    <Select label="Tipo" value={testeData?.tipo ?? 'GENERICO'} onChange={(e) => setTesteData(s => ({ ...s, tipo: e.target.value }))}>
                        <MenuItem value="GENERICO">Genérico</MenuItem>
                        <MenuItem value="ACEITE_DOCUMENTO">Aceite de documento</MenuItem>
                        <MenuItem value="CONTRATO">Contrato</MenuItem>
                        <MenuItem value="RENOVACAO_ASSINATURA">Renovação de assinatura</MenuItem>
                    </Select>
                </FormControl>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2 }}>
                <TextField size="small" fullWidth label="Destinatário (e-mail ou telefone)"
                    value={testeData?.destinatario ?? ''} onChange={handleTesteChange('destinatario')} />
                <TextField size="small" fullWidth label="Nome (variável {nome})"
                    value={testeData?.nome ?? ''} onChange={handleTesteChange('nome')} />
            </Box>

            <TextField size="small" fullWidth label="Link (variável {link})"
                value={testeData?.link ?? ''} onChange={handleTesteChange('link')} sx={{ mb: 2 }} />

            {testeData?.tipo === 'GENERICO' && (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2 }}>
                    <TextField size="small" fullWidth label="Assunto personalizado"
                        value={testeData?.assuntoCustom ?? ''} onChange={handleTesteChange('assuntoCustom')} />
                    <TextField size="small" fullWidth label="Conteúdo personalizado"
                        value={testeData?.conteudoCustom ?? ''} onChange={handleTesteChange('conteudoCustom')} />
                </Box>
            )}

            <Button variant="contained" startIcon={<SendIcon />}
                onClick={enviarTesteNotificacao} disabled={testeSending}
                sx={{ bgcolor: '#1ABB9C', '&:hover': { bgcolor: '#129A80' } }}>
                {testeSending ? 'Enviando...' : 'Enviar teste'}
            </Button>
        </Box>
    );
}
